import React, { useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import { Link as RouterLink } from 'react-router-dom';
import defaultPlaylistThumbnail from '../../assets/default_playlist_thumbnail.svg';
import CommentCount from '../common/CommentCount';
import AlbumCountWithDate from '../common/AlbumCountWithDate';
import UserWithProfile from '../common/UserWithProfile';
import CardTitle from '../common/CardTitle';
import theme from '../../theme';
import { trackHomeContentClick } from '../../lib/gaEvents';
import { CONTENT_TYPE, REFERRER_TYPE } from '../../constants/gaEnums';
import { useUserStore } from '../../stores/userStore';

const PlaylistCard = ({
  playlist,
  dateOnNewLine = false,
  actionButton,
  hideUser = false,
  hideMeta = false,
  referrerType,
  referrerSection,
  homeSection,
  thumbnailVariant = 'default',
  compactMeta = false,
  floatingActionButton = false,
  floatingActionButtonPlacement = 'center',
}) => {
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const user = useUserStore((state) => state.user);
  const [imageError, setImageError] = useState(false);
  const playlistUrl = `/lists/${playlist.slug || playlist.id}`;
  const navigateState = homeSection
    ? { referrerType: REFERRER_TYPE.HOME }
    : referrerType
      ? { referrerType, referrerSection }
      : undefined;

  const handleLinkToPlaylist = (e) => {
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      if (homeSection) {
        trackHomeContentClick(
          {
            section: homeSection,
            contentType: CONTENT_TYPE.LIST,
            contentId: playlist.id,
            contentName: playlist.title,
          },
          user,
        );
      }
      navigateWithScrollMemory(playlistUrl, navigateState ? { state: navigateState } : undefined);
    }
  };

  const handleUserClick = (userNickname) => {
    navigateWithScrollMemory(`/users/${encodeURIComponent(userNickname)}`);
  };

  const handleError = () => {
    if (!imageError) {
      setImageError(true);
    }
  };

  const thumbnailAspectRatio =
    thumbnailVariant === 'vertical'
      ? '1 / 1.34'
      : thumbnailVariant === 'square'
        ? '1 / 1'
        : '280 / 188';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: { xs: 1.25, md: 2, lg: 2.5 },
        position: 'relative',
      }}
    >
      {/* 좌측 이미지 */}
      <Box
        component={RouterLink}
        to={playlistUrl}
        state={navigateState}
        onClick={handleLinkToPlaylist}
        sx={{
          display: 'block',
          width: { xs: '34%', md: '30%' },
          minWidth: { xs: 112, md: 168 },
          maxWidth: { xs: 136, md: 180 },
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={imageError || !playlist.imageUrl ? defaultPlaylistThumbnail : playlist.imageUrl}
          alt={imageError || !playlist.imageUrl ? 'Default playlist thumbnail' : playlist.title}
          onError={handleError}
          sx={{
            width: '100%',
            aspectRatio: thumbnailAspectRatio,
            cursor: 'pointer',
            objectFit: 'cover',
            display: 'block',
            border: `1px solid ${theme.palette.grey.line}`,
          }}
        />
      </Box>

      {/* 우측 정보 영역 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'stretch',
          justifyContent: 'center',
          width: '100%',
          minWidth: 0,
          pr: floatingActionButton ? { xs: 6.5, md: 0 } : 0,
        }}
      >
        <CardTitle title={playlist.title} to={playlistUrl} onClick={handleLinkToPlaylist} />
        {!hideUser && (
          <UserWithProfile
            variant="small"
            username={playlist.username}
            profileImageUrl={playlist.userProfileImageUrl}
            userNickname={playlist.username}
            grade={playlist.userGrade}
            onClick={handleUserClick}
            sx={{
              mt: compactMeta ? { xs: 0.75, md: 1.5, lg: 2 } : { xs: 1, md: 1.5, lg: 2 },
            }}
          />
        )}

        {!hideMeta && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: floatingActionButton ? 'flex-start' : 'space-between',
              alignItems: 'center',
              mt: compactMeta ? { xs: 1.25, md: 1.5, lg: 2 } : { xs: 1.5, lg: 2 },
            }}
          >
            {/* 좌측: 앨범수, 날짜, 댓글 */}
            <Box>
              <Box display="flex" gap={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <AlbumCountWithDate
                  albumCount={playlist.type === 'TRACK' ? playlist.trackCount : playlist.albumCount}
                  date={!dateOnNewLine && playlist.createdAt}
                  type={playlist.type}
                />
                {dateOnNewLine && (
                  <>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: '8px',
                        alignSelf: 'center',
                        backgroundColor: theme.palette.grey.line,
                      }}
                    />
                    <Typography
                      fontSize={{ xs: 12, md: 13, lg: 14 }}
                      fontWeight="regular"
                      sx={{ color: theme.palette.black[4], whiteSpace: 'nowrap' }}
                    >
                      {playlist.createdAt}
                    </Typography>
                  </>
                )}
                {playlist.createdAt && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      height: '8px',
                      alignSelf: 'center',
                      backgroundColor: theme.palette.grey.line,
                    }}
                  />
                )}
                <CommentCount commentCount={playlist.commentCount} />
              </Box>
            </Box>

            {/* 우측: 버튼 */}
            {!floatingActionButton && actionButton}
          </Box>
        )}
      </Box>
      {floatingActionButton && actionButton && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: floatingActionButtonPlacement === 'top' ? 0 : '50%',
            transform: floatingActionButtonPlacement === 'top' ? 'none' : 'translateY(-50%)',
            zIndex: 2,
          }}
        >
          {actionButton}
        </Box>
      )}
    </Box>
  );
};

export default PlaylistCard;
